import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n';
import { cn } from '../../lib/utils';

interface ThemeToggleProps {
  className?: string;
  showLabels?: boolean;
  variant?: 'button' | 'dropdown';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className,
  showLabels = false,
  variant = 'button'
}) => {
  const { theme, setTheme, actualTheme } = useTheme();
  const { t } = useTranslation();

  const themes = [
    { value: 'light', labelKey: 'theme.light', label: 'Light', icon: Sun },
    { value: 'dark', labelKey: 'theme.dark', label: 'Dark', icon: Moon },
    { value: 'system', labelKey: 'theme.system', label: 'System', icon: Monitor },
  ] as const;

  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <motion.button
          className="flex items-center space-x-2 px-3 py-2 rounded-lg border bg-background hover:bg-accent transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            key={actualTheme}
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="w-4 h-4"
          >
            {actualTheme === 'dark' ? (
              <Moon className="w-4 h-4" />
            ) : (
              <Sun className="w-4 h-4" />
            )}
          </motion.div>
          {showLabels && (
            <span className="text-sm font-medium">
              {t(themes.find(thm => thm.value === theme)?.labelKey || 'theme.system', 
                 themes.find(thm => thm.value === theme)?.label || 'System')}
            </span>
          )}
        </motion.button>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center space-x-1 p-1 bg-muted rounded-lg', className)}>
      {themes.map((themeOption) => {
        const Icon = themeOption.icon;
        const isActive = theme === themeOption.value;
        
        return (
          <motion.button
            key={themeOption.value}
            className={cn(
              'relative flex items-center justify-center p-2 rounded-md transition-colors',
              'hover:bg-background/50',
              isActive && 'bg-background shadow-sm'
            )}
            onClick={() => setTheme(themeOption.value)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={t('theme.switch_to', 'Switch to {{theme}} theme', { 
              theme: t(themeOption.labelKey, themeOption.label) 
            })}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`${themeOption.value}-${actualTheme}`}
                initial={{ scale: 0.8, opacity: 0, rotate: -90 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.8, opacity: 0, rotate: 90 }}
                transition={{ 
                  type: 'spring', 
                  stiffness: 200, 
                  damping: 15,
                  duration: 0.2 
                }}
                className="w-4 h-4"
              >
                <Icon className={cn(
                  'w-4 h-4',
                  isActive ? 'text-foreground' : 'text-muted-foreground'
                )} />
              </motion.div>
            </AnimatePresence>
            
            {isActive && (
              <motion.div
                layoutId="activeTheme"
                className="absolute inset-0 bg-background rounded-md shadow-sm"
                style={{ zIndex: -1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              />
            )}
          </motion.button>
        );
      })}
    </div>
  );
};

// Simple toggle between light and dark
export const SimpleThemeToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { actualTheme, setTheme, theme } = useTheme();
  const { t } = useTranslation();

  const toggleTheme = () => {
    if (theme === 'system') {
      setTheme(actualTheme === 'dark' ? 'light' : 'dark');
    } else {
      setTheme(theme === 'dark' ? 'light' : 'dark');
    }
  };

  return (
    <motion.button
      className={cn(
        'relative p-2 rounded-lg border bg-background hover:bg-accent transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className
      )}
      onClick={toggleTheme}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      title={t('theme.switch_to_mode', 'Switch to {{mode}} mode', { 
        mode: t(actualTheme === 'dark' ? 'theme.light' : 'theme.dark', 
               actualTheme === 'dark' ? 'light' : 'dark') 
      })}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={actualTheme}
          initial={{ scale: 0, rotate: -90, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          exit={{ scale: 0, rotate: 90, opacity: 0 }}
          transition={{ 
            type: 'spring', 
            stiffness: 200, 
            damping: 15,
            duration: 0.3 
          }}
          className="w-5 h-5"
        >
          {actualTheme === 'dark' ? (
            <Sun className="w-5 h-5 text-yellow-500" />
          ) : (
            <Moon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          )}
        </motion.div>
      </AnimatePresence>
    </motion.button>
  );
};